import {
    TInterviewGoalAttachment,
    TInterviewGoalAttachmentType,
    TInterviewMessage,
} from "../model/TInterviewThread";

export const ATTACHMENT_MARKER_PREFIX = "[[interview-goal-attachment:";
const ATTACHMENT_MARKER_PATTERN =
    /^\[\[interview-goal-attachment:v1:(image|audio|video|document|link):([1-9][0-9]*)\]\]$/;

export type TInterviewMessageSegment = {
    type: "text",
    content: string,
} | {
    type: "attachment",
    attachment: TInterviewGoalAttachment,
} | {
    type: "gallery",
    attachments: TInterviewGoalAttachment[],
};

function groupConsecutiveImages(segments: TInterviewMessageSegment[]): TInterviewMessageSegment[] {
    const groupedSegments: TInterviewMessageSegment[] = [];

    for (let index = 0; index < segments.length;) {
        const segment = segments[index];
        if (segment.type !== "attachment" || segment.attachment.type !== "image") {
            groupedSegments.push(segment);
            index++;
            continue;
        }

        const images: TInterviewGoalAttachment[] = [];
        while (index < segments.length) {
            const imageSegment = segments[index];
            if (imageSegment.type !== "attachment" || imageSegment.attachment.type !== "image") {
                break;
            }
            images.push(imageSegment.attachment);
            index++;
        }

        groupedSegments.push(images.length >= 2
            ? {type: "gallery", attachments: images}
            : {type: "attachment", attachment: images[0]});
    }

    return groupedSegments;
}

export function tokenizeInterviewGoalAttachments(
    message: TInterviewMessage,
    warn: (message: string, context: {messageId: number, marker: string}) => void = console.warn,
): TInterviewMessageSegment[] {
    if (message.role !== "agent" || message.contentFormat !== "plain") {
        return [{type: "text", content: message.content}];
    }

    const attachments = new Map((message.goalAttachments ?? []).map(attachment => [attachment.id, attachment]));
    const lines = message.content.split(/\r\n|\n|\r/);
    const markerIdCounts = new Map<number, number>();
    lines.forEach(line => {
        const match = line.match(ATTACHMENT_MARKER_PATTERN);
        if (match) {
            const id = Number(match[2]);
            markerIdCounts.set(id, (markerIdCounts.get(id) ?? 0) + 1);
        }
    });
    const segments: TInterviewMessageSegment[] = [];
    let textParts: string[] = [];

    const flushText = (): void => {
        if (textParts.length > 0) {
            segments.push({type: "text", content: textParts.join("")});
            textParts = [];
        }
    };

    lines.forEach((line, index) => {
        const match = line.match(ATTACHMENT_MARKER_PATTERN);
        if (!match) {
            if (line.includes(ATTACHMENT_MARKER_PREFIX)) {
                warn("InterviewThread rendered an invalid attachment marker as text.", {
                    messageId: message.id,
                    marker: line,
                });
            }
            textParts.push(index === lines.length - 1 ? line : `${line}\n`);
            return;
        }

        const attachmentId = Number(match[2]);
        const attachment = attachments.get(attachmentId);
        if (
            markerIdCounts.get(attachmentId) !== 1
            || !attachment
            || attachment.type !== match[1] as TInterviewGoalAttachmentType
        ) {
            warn("InterviewThread rendered an unresolved attachment marker as text.", {
                messageId: message.id,
                marker: line,
            });
            textParts.push(index === lines.length - 1 ? line : `${line}\n`);
            return;
        }

        flushText();
        segments.push({type: "attachment", attachment});
    });
    flushText();

    return groupConsecutiveImages(segments);
}
